'use client';

import cn from 'classnames';
import { useMemo } from 'react';
import { Range, getTrackBackground } from 'react-range';
import { Icon } from '../Icon';
import { InputRangeProps } from './InputRange.types';

import s from './InputRange.module.css';

const InputRange = ({
  name,
  max,
  min = 0,
  value = 0,
  incrementStep = 1,
  renderOutput,
  onChange,
}: InputRangeProps) => {
  const handleChange = ([newValue]: number[]) => {
    if (onChange) {
      onChange({ name, value: newValue });
    }
  };

  const iconRender = useMemo(() => {
    switch (value) {
      case 1:
        return <Icon variant="linear" name="MildPain" size={16} color="neutral-900" />;

      case 2:
        return <Icon variant="linear" name="ModeratePain" size={16} color="neutral-900" />;

      case 3:
        return <Icon variant="linear" name="SeverePain" size={16} color="neutral-900" />;

      case 4:
        return <Icon variant="linear" name="VerySeverePain" size={16} color="neutral-900" />;

      case 5:
        return <Icon variant="linear" name="WorstPain" size={16} color="neutral-900" />;

      default:
        return <Icon variant="linear" name="NoPain" size={16} color="neutral-900" />;
    }
  }, [value]);

  return (
    <Range
      step={incrementStep}
      min={min}
      max={max}
      values={[value]}
      onChange={handleChange}
      renderTrack={({ props, children }) => (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          onMouseDown={props.onMouseDown}
          onTouchStart={props.onTouchStart}
          className={s.track}
          style={props.style}
        >
          <div
            ref={props.ref}
            className={s.progress}
            style={{
              background: getTrackBackground({
                values: [value],
                colors: ['var(--colors-iris-50)', 'var(--colors-iris-50)'],
                min,
                max,
              }),
            }}
          >
            <div
              className={cn(
                s.bar,
                { [s.mildPain]: value === 1 },
                { [s.moderatePain]: value === 2 },
                { [s.severePain]: value === 3 },
                { [s.verySeverePain]: value === 4 },
                { [s.worstPain]: value === 5 },
              )}
            />
            <div className={s.points}>
              <div className={s.point} />
              <div className={s.point} />
              <div className={s.point} />
              <div className={s.point} />
              <div className={s.point} />
              <div className={s.point} />
            </div>
            {children}
          </div>
        </div>
      )}
      renderThumb={({ props }) => (
        <div
          {...props}
          key={props.key}
          className={cn(
            s.thumb,
            { [s.noPain]: value === 0 },
            { [s.mildPain]: value === 1 },
            { [s.moderatePain]: value === 2 },
            { [s.severePain]: value === 3 },
            { [s.verySeverePain]: value === 4 },
            { [s.worstPain]: value === 5 },
          )}
          style={props.style}
        >
          <span
            className={cn(
              s.tooltip,
              { [s.noPain]: value === 0 },
              { [s.mildPain]: value === 1 },
              { [s.moderatePain]: value === 2 },
              { [s.severePain]: value === 3 },
              { [s.verySeverePain]: value === 4 },
              { [s.worstPain]: value === 5 },
            )}
          >
            {renderOutput ? (
              <span className={s.label}>{renderOutput(value)}</span>
            ) : (
              <span className={s.tooltipLabel}>{iconRender}</span>
            )}
          </span>
        </div>
      )}
    />
  );
};

export default InputRange;
